// openEHR RM model — BMM package org.openehr.rm.support.terminology
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { OpenEHRTerminologyService } from "../../term/terminology_service.ts";
import { CODE_PHRASE } from "../data_types/text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * List of identifiers for groups in the openEHR terminology.
 */
export class OPENEHR_TERMINOLOGY_GROUP_IDENTIFIERS {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Validity function to test if an identifier is in the set defined by this class.
   * @param an_id - Parameter
   * @returns Result value
   */
  valid_terminology_group_id(
    an_id: openehr_base.Boolean,
  ): openehr_base.Boolean {
    // Get terminology service and check if group exists
    const service = OpenEHRTerminologyService.getInstance();
    const idStr = typeof an_id === 'string' ? an_id : String(an_id);
    return openehr_base.Boolean.from(service.hasGroup(idStr));
  }
}

/**
 * List of identifiers for code sets in the openEHR terminology.
 */
export class OPENEHR_CODE_SET_IDENTIFIERS {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Validity function to test if an identifier is in the set defined by this class.
   * @param an_id - Parameter
   * @returns Result value
   */
  valid_code_set_id(an_id: openehr_base.String): openehr_base.Boolean {
    const service = OpenEHRTerminologyService.getInstance();
    const idStr = typeof an_id === "string" ? an_id : an_id.value;
    if (!idStr) return openehr_base.Boolean.from(false);
    return openehr_base.Boolean.from(service.hasCodeSet(idStr));
  }
}

/**
 * Defines an object providing proxy access to a terminology service.
 */
export class TERMINOLOGY_SERVICE extends OPENEHR_TERMINOLOGY_GROUP_IDENTIFIERS {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Return an interface to the terminology named name. Allowable names include:-
   *
   * * openehr,
   * * centc251,
   * * any name from are taken from the US NLM UMLS meta-data list at http://www.nlm.nih.gov/research/umls/metaa1.html
   * @param name - Parameter
   * @returns Result value
   */
  terminology(name: openehr_base.String): TERMINOLOGY_ACCESS {
    const nameStr = typeof name === "string" ? name : name.value;
    if (!nameStr) {
      throw new Error("Terminology name is required");
    }
    if (nameStr.toLowerCase() !== "openehr") {
      throw new Error(`Terminology "${nameStr}" is not supported. Only "openehr" is currently available.`);
    }
    
    return new TERMINOLOGY_ACCESS(nameStr);
  }

  /**
   * Return an interface to the code_set identified by the external identifier name (e.g.  ISO_639-1).
   * @param name - Parameter
   * @returns Result value
   */
  code_set(name: openehr_base.String): CODE_SET_ACCESS {
    const nameStr = typeof name === "string" ? name : name.value;
    if (!nameStr) throw new Error("Code set name is required");
    const service = OpenEHRTerminologyService.getInstance();
    if (!service.hasCodeSet(nameStr)) {
      throw new Error(`Code set "${nameStr}" not found`);
    }
    
    return new CODE_SET_ACCESS(nameStr);
  }

  /**
   * Return an interface to the code_set identified internally in openEHR by id.
   *
   * @param id - Parameter
   * @returns Result value
   */
  code_set_for_id(id: openehr_base.String): CODE_SET_ACCESS {
    const idStr = typeof id === "string" ? id : id.value;
    if (!idStr) throw new Error("Code set id is required");
    const service = OpenEHRTerminologyService.getInstance();
    if (!service.hasCodeSet(idStr)) {
      throw new Error(`Code set with id "${idStr}" not found`);
    }
    
    return new CODE_SET_ACCESS(idStr);
  }

  /**
   * True if terminology named name known by this service. Allowable names include:-
   *
   * *  openehr
   * * centc251
   * * any name from are taken from the US NLM UMLS meta-data list at       http://www.nlm.nih.gov/research/umls/metaa1.html
   * @param name - Parameter
   * @returns Result value
   */
  has_terminology(name: openehr_base.String): openehr_base.Boolean {
    const service = OpenEHRTerminologyService.getInstance();
    const nameStr = typeof name === "string" ? name : name.value;
    if (!nameStr) return openehr_base.Boolean.from(false);
    return openehr_base.Boolean.from(service.hasTerminology(nameStr));
  }

  /**
   * True if code_set linked to internal name (e.g. languages ) is available.
   * @param name - Parameter
   * @returns Result value
   */
  has_code_set(name: openehr_base.String): openehr_base.Boolean {
    const service = OpenEHRTerminologyService.getInstance();
    const nameStr = typeof name === "string" ? name : name.value;
    if (!nameStr) return openehr_base.Boolean.from(false);
    return openehr_base.Boolean.from(service.hasCodeSet(nameStr));
  }

  /**
   * Set of all terminology identifiers known in the terminology service. Values from the US NLM UMLS meta-data list at:- http://www.nlm.nih.gov/research/umls/metaa1.html
   * @returns Result value
   */
  terminology_identifiers(): openehr_base.String {
    // Currently only "openehr" is supported
    return openehr_base.String.from("openehr");
  }

  /**
   * Set of all code set identifiers known in the terminology service.
   *
   * @returns Result value
   */
  openehr_code_sets(): undefined {
    // Get terminology service
    const service = OpenEHRTerminologyService.getInstance();
    const identifiers = service.getCodeSetIdentifiers();
    // Return as undefined per signature (should be a Set but type is undefined)
    return identifiers as any;
  }

  /**
   * Set of all code sets identifiers for which there is an internal openEHR name; returned as a Map of ids keyed by internal name.
   * @returns Result value
   */
  code_set_identifiers(): openehr_base.String {
    // Get terminology service
    const service = OpenEHRTerminologyService.getInstance();
    const identifiers = service.getCodeSetIdentifiers();
    // Return as comma-separated string
    return openehr_base.String.from(identifiers.join(", "));
  }
}

/**
 * Defines an object providing proxy access to a code_set.
 */
export class CODE_SET_ACCESS {
  private codeSetId: string;
  private language: string;
  
  constructor(codeSetIdOrInit?: string | RmInit, language: string = "en") {
    if (codeSetIdOrInit !== undefined && typeof codeSetIdOrInit === "object" && codeSetIdOrInit !== null) {
      this.codeSetId = "";
      this.language = language;
      applyRmInit(this, codeSetIdOrInit);
      return;
    }
    this.codeSetId = codeSetIdOrInit as string;
    this.language = language;
  }
  
  /**
   * External identifier of this code set.
   * @returns Result value
   */
  id(): openehr_base.String {
    return openehr_base.String.from(this.codeSetId);
  }

  /**
   * Return all codes known in this code set.
   * @returns Result value
   */
  all_codes(): CODE_PHRASE {
    // Get codes from terminology service
    const service = OpenEHRTerminologyService.getInstance();
    const codes = service.getAllCodes(this.codeSetId, this.language);
    
    // Return first code as CODE_PHRASE (API design issue - should return array)
    const result = new CODE_PHRASE();
    if (codes.length > 0) {
      result.code_string = codes[0];
    }
    return result;
  }

  /**
   * True if code set knows about 'a_lang' .
   * @param a_lang - Parameter
   * @returns Result value
   */
  has_lang(a_lang: openehr_base.String): openehr_base.Boolean {
    // Check if the language is supported (en, es, pt currently)
    const lang = typeof a_lang === "string" ? a_lang : a_lang.value;
    if (!lang) return openehr_base.Boolean.from(false);
    const supportedLangs = ["en", "es", "pt"];
    return openehr_base.Boolean.from(supportedLangs.includes(lang));
  }

  /**
   * True if code set knows about  'a_code'.
   * @param a_code - Parameter
   * @returns Result value
   */
  has_code(a_code: openehr_base.String): openehr_base.Boolean {
    // Get codes from terminology service
    const service = OpenEHRTerminologyService.getInstance();
    const codes = service.getAllCodes(this.codeSetId, this.language);
    const codeStr = typeof a_code === "string" ? a_code : a_code.value;
    if (!codeStr) return openehr_base.Boolean.from(false);
    return openehr_base.Boolean.from(codes.includes(codeStr));
  }
}

/**
 * Defines an object providing proxy access to a terminology.
 */
export class TERMINOLOGY_ACCESS {
  private terminologyId: string;
  private language: string;
  
  constructor(terminologyIdOrInit?: string | RmInit, language: string = "en") {
    if (terminologyIdOrInit !== undefined && typeof terminologyIdOrInit === "object" && terminologyIdOrInit !== null) {
      this.terminologyId = "";
      this.language = language;
      applyRmInit(this, terminologyIdOrInit);
      return;
    }
    this.terminologyId = terminologyIdOrInit as string;
    this.language = language;
  }
  
  /**
   * Identification of this Terminology.
   * @returns Result value
   */
  id(): openehr_base.String {
    return openehr_base.String.from(this.terminologyId);
  }

  /**
   * Return all codes known in this terminology.
   * @returns Result value
   */
  all_codes(): CODE_PHRASE {
    // Get all group identifiers from terminology service
    const service = OpenEHRTerminologyService.getInstance();
    const groups = service.getGroupIdentifiers();
    
    // Return first group as CODE_PHRASE (API design issue - should return array)
    const result = new CODE_PHRASE();
    if (groups.length > 0) {
      result.code_string = groups[0];
    }
    return result;
  }

  /**
   * Return all codes under grouper 'a_group_id' from this terminology.
   * 
   * NOTE: Per openEHR specification, this method should return a Set<CODE_PHRASE>.
   * Due to TypeScript API design limitations, only the first code is returned.
   * This is a known limitation that should be addressed in a future API revision.
   * For complete code retrieval, use the OpenEHRTerminologyService directly.
   * 
   * @param a_group_id - The group identifier (e.g., "audit_change_type", "attestation_reason")
   * @returns CODE_PHRASE containing the first code in the group
   */
  codes_for_group_id(a_group_id: openehr_base.String): CODE_PHRASE {
    const groupId = typeof a_group_id === 'string' ? a_group_id : (a_group_id?.value ?? "");
    const service = OpenEHRTerminologyService.getInstance();
    
    // Get all codes for the specified group
    const codes = service.getCodesForGroup(groupId, this.language);
    
    // Return first code as CODE_PHRASE (API limitation - spec says should return Set)
    const result = new CODE_PHRASE();
    result.terminology_id = new openehr_base.TERMINOLOGY_ID();
    result.terminology_id.value = this.terminologyId;
    
    if (codes.length > 0) {
      result.code_string = codes[0];
    }
    return result;
  }

  /**
   * Return all codes under grouper whose name in 'a_lang' is 'a_name' from this terminology.
   * 
   * NOTE: This method returns CODE_PHRASE per the openEHR specification, but conceptually
   * should return a collection of codes. Due to API limitations, only the first code is
   * returned. If the group is not found, an empty CODE_PHRASE is returned (no code_string).
   * Callers should check if code_string is set to determine if the group was found and has codes.
   * 
   * @param a_lang - Language code (e.g., "en", "es")
   * @param a_name - Group name in the specified language
   * @returns CODE_PHRASE with first code, or empty CODE_PHRASE if group not found
   */
  codes_for_group_name(
    a_lang: openehr_base.String,
    a_name: openehr_base.String,
  ): CODE_PHRASE {
    const lang = typeof a_lang === 'string' ? a_lang : (a_lang?.value ?? "en");
    const name = typeof a_name === 'string' ? a_name : (a_name?.value ?? "");
    const service = OpenEHRTerminologyService.getInstance();
    
    // Get group ID by name in the specified language
    const groupId = service.getGroupIdByName(name, lang);
    
    if (!groupId) {
      // Group not found - return empty CODE_PHRASE
      // Callers can detect this by checking if code_string is undefined/empty
      const result = new CODE_PHRASE();
      result.terminology_id = new openehr_base.TERMINOLOGY_ID();
      result.terminology_id.value = this.terminologyId;
      return result;
    }
    
    // Delegate to codes_for_group_id
    return this.codes_for_group_id(openehr_base.String.from(groupId));
  }

  /**
   * True if 'a_code' is known in group 'group_id' in the openEHR terminology.
   * @param group_id - The group identifier
   * @param a_code - The code to check
   * @returns Result value
   */
  has_code_for_group_id(
    group_id: openehr_base.String,
    a_code: openehr_base.String
  ): openehr_base.Boolean {
    const gid = typeof group_id === 'string' ? group_id : (group_id?.value ?? "");
    const codeStr = typeof a_code === 'string' ? a_code : (a_code?.value ?? "");
    
    const service = OpenEHRTerminologyService.getInstance();
    const codes = service.getCodesForGroup(gid, this.language);
    
    return openehr_base.Boolean.from(codes.includes(codeStr));
  }

  /**
   * Return the rubric (human-readable term) for code 'a_code' in the configured language.
   * Falls back to English if the translation is not available.
   * @param a_code - The code to look up
   * @returns Result value
   */
  rubric_for_code(a_code: openehr_base.String): openehr_base.String {
    const code = typeof a_code === 'string' ? a_code : (a_code?.value ?? "");
    const service = OpenEHRTerminologyService.getInstance();
    
    // Try to get rubric in configured language
    let rubric = service.getRubricForCode(code, this.language);
    
    // Fall back to English if not found
    if (!rubric && this.language !== "en") {
      rubric = service.getRubricForCode(code, "en");
    }
    
    // Return code itself if no rubric found
    return openehr_base.String.from(rubric ?? code);
  }
}
